/**
 * transactionPick — a bank transaction, as one row of a picker.
 *
 * THIS IS WHAT REPLACED A SHEET INSIDE A SHEET. `TransactionSelector` opens a 500px right-hand
 * Sheet on top of whatever Sheet was already holding the form, draws each candidate as a 96px
 * bordered card with a coloured type pill, and offers a second Sheet on top of that to create one.
 * Five setup rows used it. What a row actually has to say is the amount, the day and what the bank
 * called it — three facts that fit on one line — so this module derives that line and the picker
 * draws it, and the sheets do not open on the setup canvas any more.
 *
 * THE AMOUNT LEADS, BECAUSE THE AMOUNT IS WHAT IS BEING RECOGNISED. An admin linking the payment
 * for a roadworthiness test is looking for "the 250 from Tuesday", never for a description they
 * did not write; the date and the bank's own text follow it as the tie-breakers they are. The
 * three are handed over SEPARATELY rather than pre-joined into one string, because a row that
 * joins them can only ever truncate from the right — which is how `ATM WD GTB000000000079 ACC…`
 * came to push the amount out of its own row.
 *
 * PURE, AND REACT-FREE — vitest runs node-env over `.ts` only.
 */

import { format } from 'date-fns';

import { businessDay, formatBusinessDate, formatCurrency } from '@/lib/format';

/** The shape `get_unassigned_transactions` returns, and the one `transactions` selects give back. */
export interface PickableTransaction {
    id: number;
    amount: number;
    transaction_at: string;
    description: string | null;
    type: string;
    /** GHS or USD. The pool holds both, and a USD payment printed as cedis is different money. */
    currency?: string | null;
}

/** `GH₵250.00`, or `US$3,000.00` for a USD line — the fact the eye is scanning for, on its own. */
export function transactionLabel(transaction: PickableTransaction): string {
    return formatCurrency(transaction.amount ?? 0, transaction.currency || 'GHS');
}

/** `Mar 4, 2026` — the day the money moved, or '' where the row carries no date. */
export function transactionDate(transaction: PickableTransaction): string {
    return transaction.transaction_at ? formatBusinessDate(transaction.transaction_at) : '';
}

/**
 * Whatever the bank called it — `ATM WD GTB000000000079 ACC`, `MTN MOMO TRF`.
 *
 * A transaction with no description says so rather than leaving its column empty, which reads as
 * text that failed to load rather than as text that was never there.
 */
export function transactionDescription(transaction: PickableTransaction): string {
    return transaction.description?.trim() || 'no description';
}

/** Amount, date and the bank's text — everything a row can be found by. */
export function transactionSearchText(transaction: PickableTransaction): string {
    // The comma-less shape is deliberate — an operator types "Mar 4 2026" — so this formats the
    // business day through date-fns rather than reusing `formatBusinessDate`, which punctuates it.
    const day = businessDay(transaction.transaction_at);
    return [
        String(transaction.amount ?? ''),
        day ? format(day, 'MMM d yyyy') : '',
        transaction.description ?? '',
        `#${transaction.id}`,
    ]
        .filter(Boolean)
        .join(' ');
}

/** What the trigger reads once one is linked: `GH₵250.00 · Mar 4, 2026`. */
export function transactionTriggerText(transaction: PickableTransaction | null): string | null {
    if (!transaction) return null;
    const dated = transaction.transaction_at ? formatBusinessDate(transaction.transaction_at) : '';
    return [transactionLabel(transaction), dated].filter(Boolean).join(' · ');
}

/**
 * The pool, with the already-linked transaction restored to the top of it.
 *
 * A LINKED TRANSACTION IS NO LONGER UNASSIGNED, which means the list a picker reads can never
 * contain the row it is currently showing. Without this the trigger reads the transaction and the
 * open list denies it exists — and the tick that says "this is the one you picked" never appears.
 */
export function withSelectedTransaction(
    pool: PickableTransaction[] | undefined,
    selected: PickableTransaction | null | undefined,
): PickableTransaction[] {
    const rows = [...(pool ?? [])];
    if (selected && !rows.some((row) => row.id === selected.id)) rows.unshift(selected);
    return rows;
}

/** The refusal for a new transaction that cannot be created, or null. */
export function validateNewTransaction(draft: { amount: string; dated: string }): string | null {
    const amount = parseFloat(draft.amount);
    if (draft.amount.trim() === '' || isNaN(amount) || amount <= 0) {
        return 'Enter the amount that left the account.';
    }
    if (!draft.dated.trim()) return 'Enter the date the money moved.';
    return null;
}
